import _ from 'lodash';

import { getGravatarUrl } from '@zapier/common-utils';
import { constants as teamConstants } from '@zapier/team';
import {
  EntityActions,
  buildEntityActions,
  buildEntityApi,
  buildEntitySelectors,
} from '@zapier/toolbox-redux-framework';

import * as Account from './Account';
import * as Profile from './Profile';

import partialWithDefault from '../utils/partialWithDefault';

export const type = 'member';

export const api = buildEntityApi(type, (path, getState) => {
  const currentAccountId = Account.selectors.all.currentAccountId(getState());
  return `/api/v3/accounts/${currentAccountId}/members/${path}`;
});

export const selectors = buildEntitySelectors(type, sel => {
  sel.all.shouldLoad = sel.all.isUnloaded;
  sel.avatarUrl = _.flow(_.partial(sel.dataItem, 'email_hash'), getGravatarUrl);
  sel.email = partialWithDefault(sel.dataItem, ['email'], '');
  sel.id = partialWithDefault(sel.dataItem, ['id'], '');
  sel.name = partialWithDefault(sel.dataItem, ['name'], '');
  sel.numTasks = partialWithDefault(sel.dataItem, ['num_tasks'], 0);
  sel.numZaps = partialWithDefault(sel.dataItem, ['num_zaps'], 0);
  sel.role = _.partial(sel.dataItem, 'role');
  const hasRole = (role, entity) => sel.role(entity) === role;
  sel.isAdmin = _.partial(hasRole, teamConstants.roles.admin);
  sel.isMember = _.partial(hasRole, teamConstants.roles.member);
  sel.isOwner = _.partial(hasRole, teamConstants.roles.owner);
  sel.isAdminOrOwner = entity => sel.isAdmin(entity) || sel.isOwner(entity);
  sel.normalizedName = entity => {
    // we have to trim the name first since it will come in as ' ' when it is empty
    return (sel.name(entity).trim() || sel.email(entity)).trim();
  };
  sel.normalizedNameLower = entity => sel.normalizedName(entity).toLowerCase();
  sel.all.currentMember = state => {
    const id = Profile.selectors.all.currentProfileId(state);
    const members = sel.all.entities(state);
    return members.find(m => sel.id(m) === id);
  };
  sel.all.canAdminAccount = state => {
    const member = sel.all.currentMember(state);
    return sel.isAdminOrOwner(member);
  };
});

export const actions = buildEntityActions(
  type,
  selectors,
  api,
  entityActions => {
    entityActions.loadCollectionIfTeamAccount = (...args) => (
      dispatch,
      getState
    ) => {
      const currentAccount = Account.selectors.all.currentAccount(getState());
      return Account.selectors.isOrWasTeam(currentAccount)
        ? dispatch(entityActions.loadCollection(...args))
        : Promise.resolve();
    };

    entityActions.setMemberRole = (id, role, messages) => dispatch => {
      return dispatch(
        EntityActions.saveEntity(type, () => ({ role }), api.save, messages, id)
      );
    };
  }
);
